import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockIcon from "@mui/icons-material/Lock";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import MailLockIcon from "@mui/icons-material/MailLock";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ptBR } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { IInstrument } from "../../clients/answeringClient";
import {
  CreateAssignmentModalController,
  CreateAssignmentModalControllerDelegate,
  IUser,
  UserEntry,
} from "./controller";
import { trackGtagEvent } from "../../utils/analytics";
import { environment } from "../../utils/environment";

interface State {
  isLoading: boolean;
  errorMessage: string;
  submissionPhase: "idle" | "creating" | "inviting" | "done";
  inviteLog: {
    email: string;
    name: string;
    done: boolean;
    error?: string;
    /** Delivery outcome for this row; `null` = no e-mail was owed. */
    emailDelivered?: boolean | null;
  }[];
  isSearching: boolean;
  instruments: IInstrument[];
  selectedInstrument: IInstrument | null;
  inputValue: string;
  allowFeedback: boolean;
  selectedDate?: Date;
  assignmentName: string;
  // user panel
  usersInput: string;
  usersSearchResults: IUser[];
  usersLoading: boolean;
  usersToAssign: UserEntry[];
  activeUserTab: number;
  newUserFirstName: string;
  newUserLastName: string;
  newUserEmail: string;
  newUserRecaptchaToken: string;
}

interface Props {
  delegate: CreateAssignmentModalDelegate;
}

export interface CreateAssignmentModalDelegate {
  shouldCloseModal: () => void;
  applicationShouldLogout: () => void;
  didCreateAssignment: (
    assignmentId: number,
    payload: { name: string; instrumentId: number; dueDate: Date }
  ) => void;
}

class CreateAssignmentModal
  extends React.Component<Props, State>
  implements CreateAssignmentModalControllerDelegate {
  controller: CreateAssignmentModalController;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private usersFetchDebounce?: NodeJS.Timeout;
  private closeTimer?: NodeJS.Timeout;
  private recaptchaRef = React.createRef<ReCAPTCHA>();

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      errorMessage: "",
      submissionPhase: "idle" as const,
      inviteLog: [],
      isSearching: false,
      instruments: [],
      selectedInstrument: null,
      inputValue: "",
      allowFeedback: false,
      selectedDate: undefined,
      assignmentName: "",
      usersInput: "",
      usersSearchResults: [],
      usersLoading: false,
      usersToAssign: [],
      activeUserTab: 0,
      newUserFirstName: "",
      newUserLastName: "",
      newUserEmail: "",
      newUserRecaptchaToken: "",
    };
    this.controller = new CreateAssignmentModalController(this);
  }

  // delegate
  didGetInstruments(instruments: IInstrument[]) {
    this.setState({ isLoading: false, isSearching: false, instruments });
  }

  didGetUsers(users: IUser[]) {
    this.setState({ usersSearchResults: users, usersLoading: false });
  }

  async didCreateAssignment(
    assignmentId: number,
    payload: { name: string; instrumentId: number; dueDate: Date }
  ) {
    const fbq = typeof window !== "undefined" ? window.fbq : undefined;
    if (
      typeof window !== "undefined" &&
      typeof document !== "undefined" &&
      document.cookie.includes("catvanteAnalyticsCookiesAccepted=true") &&
      fbq
    ) {
      fbq("trackCustom", "ApplicationCreated", {
        application_id: assignmentId ? String(assignmentId) : "",
        name: payload.name,
        instrument_id: payload.instrumentId,
        deadline: payload.dueDate.toISOString(),
      });
    }

    trackGtagEvent("application_created", {
      application_id: assignmentId ? String(assignmentId) : "",
      name: payload.name,
      instrument_id: payload.instrumentId,
      deadline: payload.dueDate.toISOString(),
    });

    const users = this.state.usersToAssign;
    if (users.length > 0) {
      const token = localStorage.getItem("AccessToken")!;
      this.setState({
        submissionPhase: "inviting",
        inviteLog: users.map((u) => ({
          email: u.email,
          name: `${u.firstName} ${u.lastName}`,
          done: false,
        })),
      });

      let hadErrors = false;
      // Tracked apart from hadErrors: the link succeeded, so this is not an
      // error — but it is the one thing the psychologist has to act on, and
      // the auto-close below would take it off screen before it was read.
      let undeliveredEmails = false;
      for (let i = 0; i < users.length; i++) {
        try {
          const emailDelivered = await this.controller.assignSingleUser(
            token,
            users[i],
            assignmentId
          );
          if (emailDelivered === false) {
            undeliveredEmails = true;
          }
          this.setState((prev) => {
            const log = [...prev.inviteLog];
            log[i] = { ...log[i], done: true, emailDelivered };
            return { inviteLog: log };
          });
        } catch (err: any) {
          hadErrors = true;
          const error = err?.response?.data?.message || "Erro ao associar respondente";
          this.setState((prev) => {
            const log = [...prev.inviteLog];
            log[i] = { ...log[i], done: true, error };
            return { inviteLog: log };
          });
        }
      }

      this.setState({ submissionPhase: "done" });
      if (!hadErrors && !undeliveredEmails) {
        this.closeTimer = setTimeout(() => {
          this.props.delegate.didCreateAssignment(assignmentId, payload);
        }, 3000);
      }
      return;
    }

    this.setState({ submissionPhase: "done" });
    this.closeTimer = setTimeout(() => {
      this.props.delegate.didCreateAssignment(assignmentId, payload);
    }, 3000);
  }

  genericError(message: string) {
    this.setState({ submissionPhase: "idle", errorMessage: message });
  }

  applicationShouldLogout() {
    this.setState({ submissionPhase: "idle" });
    this.props.delegate.applicationShouldLogout();
  }

  componentDidMount() {
    const token = localStorage.getItem("AccessToken")!;
    this.controller.searchInstruments(token);
  }

  componentWillUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    clearTimeout(this.usersFetchDebounce!);
    clearTimeout(this.closeTimer!);
  }

  handleInputChange(_event: React.SyntheticEvent, value: string) {
    this.setState({ inputValue: value, isSearching: true });
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.searchInstruments(token, value);
    }, 350);
  }

  handleChangeDueDate(date: any) {
    this.setState({ selectedDate: date && date.$d });
  }

  handleChangeName(event: any) {
    this.setState({ assignmentName: event.target.value });
  }

  didClickClose() {
    this.props.delegate.shouldCloseModal();
  }

  didClickCreateAssignment() {
    const { selectedInstrument, selectedDate, assignmentName } = this.state;
    if (!selectedInstrument || !selectedDate) return;

    this.setState({ submissionPhase: "creating", errorMessage: "" });
    const token = localStorage.getItem("AccessToken")!;
    this.controller.createAssignment(
      token,
      selectedDate,
      selectedInstrument.id,
      assignmentName,
      this.state.allowFeedback
    );
  }

  private searchUsers(filter: string) {
    this.setState({ usersLoading: true });
    clearTimeout(this.usersFetchDebounce!);
    this.usersFetchDebounce = setTimeout(() => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.searchUsers(token, filter.trim() || undefined);
    }, 300);
  }

  private addExistingUserToQueue(user: IUser) {
    const { selectedInstrument, usersToAssign } = this.state;
    if (selectedInstrument && usersToAssign.length >= selectedInstrument.quantity) return;
    if (usersToAssign.some((u) => u.id === user.uid || u.email === user.email)) return;
    this.setState((prev) => ({
      usersToAssign: [
        ...prev.usersToAssign,
        {
          id: user.uid,
          type: "existing",
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          isInvitation: user.isInvitation,
        },
      ],
      usersInput: "",
      usersSearchResults: [],
    }));
  }

  private addNewUserToQueue() {
    const { newUserFirstName, newUserLastName, newUserEmail, newUserRecaptchaToken, selectedInstrument, usersToAssign } = this.state;
    if (!newUserFirstName || !newUserLastName || !newUserEmail || !newUserRecaptchaToken) return;
    if (selectedInstrument && usersToAssign.length >= selectedInstrument.quantity) return;
    if (usersToAssign.some((u) => u.email === newUserEmail)) return;
    this.setState((prev) => ({
      usersToAssign: [
        ...prev.usersToAssign,
        {
          id: `new-${Date.now()}`,
          type: "new",
          firstName: newUserFirstName,
          lastName: newUserLastName,
          email: newUserEmail,
          recaptchaToken: newUserRecaptchaToken,
        },
      ],
      newUserFirstName: "",
      newUserLastName: "",
      newUserEmail: "",
      newUserRecaptchaToken: "",
    }), () => {
      this.recaptchaRef.current?.reset();
    });
  }

  private removeFromQueue(id: string) {
    this.setState((prev) => ({
      usersToAssign: prev.usersToAssign.filter((u) => u.id !== id),
    }));
  }

  /**
   * WHICH e-mail this queued person receives — not whether, because since the
   * invite consolidation every state here is notified.
   *
   * `code` carries the access codes and lets the person answer without
   * finishing registration; `notice` names the newly available instrument for
   * someone who already signs in with e-mail and password.
   *
   * A `new` entry is neither: its address is typed in this modal, and whether
   * it already belongs to an account is only discovered when the invite route
   * looks it up — an existing account there gets the plain notice with no code
   * at all. Collapsing it into `code`, as this used to, promised a credential
   * that half the branch never receives.
   */
  private emailKind(user: UserEntry): "code" | "notice" | "unknown" {
    if (user.type === "new") return "unknown";
    return user.isInvitation === true ? "code" : "notice";
  }

  /**
   * Per-row indicator, placed just before the remove button.
   *
   * The icon has to carry the meaning on its own — shape and colour — because
   * the tooltip is hover-only and simply absent on touch; `titleAccess` gives
   * screen readers the same sentence.
   */
  renderEmailStatusIcon(user: UserEntry) {
    const kind = this.emailKind(user);
    const label =
      kind === "code"
        ? "Receberá um e-mail com o código de acesso e poderá responder sem finalizar o cadastro."
        : kind === "notice"
          ? "Já possui conta: receberá um e-mail avisando do instrumento e entra com e-mail e senha."
          : "Se o e-mail ainda não tiver conta, recebe o código de acesso; se já tiver, recebe o aviso do convite.";

    // Three glyphs, not three colours: the tooltip is hover-only and absent on
    // touch, so the shape has to carry the distinction by itself.
    const icon =
      kind === "code" ? (
        <MailLockIcon
          titleAccess={label}
          fontSize="small"
          color="info"
          sx={{ flexShrink: 0 }}
        />
      ) : kind === "notice" ? (
        <MailOutlineIcon
          titleAccess={label}
          fontSize="small"
          color="action"
          sx={{ flexShrink: 0 }}
        />
      ) : (
        <ForwardToInboxIcon
          titleAccess={label}
          fontSize="small"
          color="info"
          sx={{ flexShrink: 0 }}
        />
      );

    return (
      <Tooltip title={label} arrow placement="left">
        {icon}
      </Tooltip>
    );
  }

  renderInviteLog() {
    const { inviteLog, submissionPhase } = this.state;
    const allDone = inviteLog.every((e) => e.done);
    const hasErrors = inviteLog.some((e) => e.error);
    // Linked, but nobody was told. Not an error — and precisely why the title
    // must stop short of the unqualified "Respondentes associados!".
    const hasUndelivered = inviteLog.some((e) => e.emailDelivered === false);

    const title =
      allDone && submissionPhase === "done"
        ? hasUndelivered
          ? "Respondentes associados, com e-mails não enviados"
          : "Respondentes associados!"
        : "Associando respondentes...";

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
          {title}
        </Typography>
        <Stack direction="column" gap={1.5}>
          {inviteLog.map((entry, i) => (
            <Box key={i} display="flex" flexDirection="column" gap={0.25}>
              <Box display="flex" alignItems="center" gap={1.5}>
                {/* Four outcomes, four glyphs: the link can fail (error), or
                    succeed with the e-mail delivered, not delivered, or never
                    owed. Collapsing the middle two into one green tick is what
                    hid a linked respondent nobody had written to. */}
                {!entry.done ? (
                  <CircularProgress size={18} sx={{ flexShrink: 0 }} />
                ) : entry.error ? (
                  <CancelIcon sx={{ color: "error.main", fontSize: 20, flexShrink: 0 }} />
                ) : entry.emailDelivered === false ? (
                  <MarkEmailUnreadIcon
                    sx={{ color: "warning.main", fontSize: 20, flexShrink: 0 }}
                  />
                ) : entry.emailDelivered === null ? (
                  <InfoOutlinedIcon
                    sx={{ color: "info.main", fontSize: 20, flexShrink: 0 }}
                  />
                ) : (
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: 20, flexShrink: 0 }} />
                )}
                <Typography variant="body2">
                  {entry.name}{" "}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({entry.email})
                  </Typography>
                </Typography>
              </Box>
              {entry.error && (
                <Typography variant="caption" color="error.main" sx={{ pl: "33.5px" }}>
                  {entry.error}
                </Typography>
              )}
              {!entry.error && entry.emailDelivered === false && (
                <Typography variant="caption" color="warning.main" sx={{ pl: "33.5px" }}>
                  Associado, mas o e-mail não foi enviado — reenvie por "Enviar
                  acesso por e-mail".
                </Typography>
              )}
              {!entry.error && entry.emailDelivered === null && (
                <Typography variant="caption" color="info.main" sx={{ pl: "33.5px" }}>
                  Associado. Nenhum e-mail era devido para este respondente.
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
        {(hasErrors || hasUndelivered) && submissionPhase === "done" && (
          <Button variant="contained" onClick={() => this.didClickClose()} sx={{ alignSelf: "flex-end", mt: 1 }}>
            Fechar
          </Button>
        )}
      </Box>
    );
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderUserPanel() {
    const {
      usersSearchResults,
      usersInput,
      usersLoading,
      usersToAssign,
      activeUserTab,
      newUserFirstName,
      newUserLastName,
      newUserEmail,
      selectedInstrument,
    } = this.state;

    const isLocked = !selectedInstrument;
    const hasNoLicenses = !!selectedInstrument && selectedInstrument.quantity < 1;
    const maxUsers = selectedInstrument?.quantity ?? 0;
    const isFull = !isLocked && !hasNoLicenses && usersToAssign.length >= maxUsers;

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
            Respondentes associados (opcional)
          </Typography>
          <Tooltip
            title="Agora é possível associar respondentes diretamente na criação da aplicação, caso haja licenças disponíveis."
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  background: "#e4e8ef",
                  color: "#5d7494ff",
                  fontSize: "0.75rem",
                },
              },
              arrow: {
                sx: { color: "#e4e8ef" },
              },
            }}
          >
            <Chip
              label="Novo"
              size="small"
              icon={<AutoAwesomeIcon />}
              sx={{
                height: 20,
                fontSize: "0.65rem",
                cursor: "default",
                background: "linear-gradient(90deg, #a855f7, #3b82f6, #06b6d4, #10b981, #3b82f6, #a855f7)",
                backgroundSize: "300% 100%",
                animation: "geminiGradient 10s linear infinite",
                color: "white",
                border: "none",
                "& .MuiChip-label": { color: "white" },
                "& .MuiChip-icon": { fontSize: "0.75rem", color: "white" },
                "@keyframes geminiGradient": {
                  "0%": { backgroundPosition: "0% 50%" },
                  "100%": { backgroundPosition: "300% 50%" },
                },
              }}
            />
          </Tooltip>
        </Box>

        {isLocked ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={{ flex: 1, minHeight: 200, color: "text.disabled" }}
          >
            <LockIcon sx={{ fontSize: 38 }} />
            <Typography variant="body2" color="text.disabled" align="center">
              Selecione um instrumento para liberar a alocação de respondentes
            </Typography>
          </Box>
        ) : (
          <>
            {hasNoLicenses && (
              <Alert severity="error">
                Este instrumento não possui licenças disponíveis para alocar. Adquira mais licenças para continuar.
              </Alert>
            )}

            {!hasNoLicenses && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="caption"
                    color={isFull ? "error.main" : "text.secondary"}
                  >
                    {usersToAssign.length} de {maxUsers} licença{maxUsers !== 1 ? "s" : ""} disponíve{maxUsers !== 1 ? "is" : "l"}
                  </Typography>
                  {isFull && (
                    <Typography variant="caption" color="error.main" fontWeight="bold">
                      Limite atingido
                    </Typography>
                  )}
                </Box>

                <Tabs
                  value={activeUserTab}
                  onChange={(_, val) => this.setState({ activeUserTab: val })}
                  variant="fullWidth"
                  sx={{
                    bgcolor: "grey.200",
                    borderRadius: 1,
                    "& .MuiTab-root": { borderRadius: 1 },
                  }}
                >
                  <Tab label="Meus respondentes" sx={{ textTransform: "none" }} />
                  <Tab label="Novo respondente" sx={{ textTransform: "none" }} />
                </Tabs>

                {activeUserTab === 0 && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Todo respondente adicionado recebe um e-mail. Quem ainda não
                    finalizou o cadastro recebe o código de acesso; quem já
                    possui conta recebe um aviso do instrumento e entra com
                    e-mail e senha.
                  </Alert>
                )}

                {activeUserTab === 0 && (
                  <Autocomplete
                    disabled={isFull}
                    options={usersSearchResults}
                    getOptionLabel={(user: IUser) =>
                      `${user.firstName} ${user.lastName} (${user.email})`
                    }
                    value={null}
                    inputValue={usersInput}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") {
                        this.setState({ usersInput: value }, () =>
                          this.searchUsers(value)
                        );
                      } else if (reason === "clear") {
                        this.setState({ usersInput: "" });
                      }
                    }}
                    onChange={(_, user: IUser | null) => {
                      if (user) this.addExistingUserToQueue(user);
                    }}
                    loading={usersLoading}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(a, b) => a.uid === b.uid}
                    noOptionsText={
                      usersInput.length === 0
                        ? "Digite para buscar respondentes"
                        : "Nenhum respondente encontrado"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar respondente"
                        helperText={isFull ? "Limite de licenças atingido" : "Selecione para adicionar à lista abaixo"}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{ style: { maxHeight: 200 } }}
                  />
                )}

                {activeUserTab === 1 && (
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {/* With an application attached the
                        invite pipeline is moving to sending the access-code
                        e-mail INSTEAD of the registration one. Until that ships
                        both go out, so this modal must not reach production
                        ahead of the backend. */}
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      <strong>Se o e-mail ainda não tiver conta</strong>, o
                      respondente receberá um e-mail com o código de acesso e
                      poderá responder sem finalizar o cadastro.{" "}
                      <strong>Se já tiver conta</strong>, receberá apenas o aviso
                      do convite e entra com e-mail e senha.
                    </Alert>
                    <TextField
                      label="Nome"
                      required
                      disabled={isFull}
                      value={newUserFirstName}
                      onChange={(e) =>
                        this.setState({ newUserFirstName: e.target.value })
                      }
                    />
                    <TextField
                      label="Sobrenome"
                      required
                      disabled={isFull}
                      value={newUserLastName}
                      onChange={(e) =>
                        this.setState({ newUserLastName: e.target.value })
                      }
                    />
                    <TextField
                      label="E-mail"
                      required
                      disabled={isFull}
                      value={newUserEmail}
                      onChange={(e) =>
                        this.setState({
                          newUserEmail: e.target.value.toLowerCase().trim(),
                        })
                      }
                    />
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <ReCAPTCHA
                        ref={this.recaptchaRef}
                        sitekey={environment.google.recaptchaToken}
                        onChange={(token) =>
                          this.setState({ newUserRecaptchaToken: token ?? "" })
                        }
                        onExpired={() => this.setState({ newUserRecaptchaToken: "" })}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      disabled={
                        isFull ||
                        !newUserFirstName ||
                        !newUserLastName ||
                        !newUserEmail ||
                        !this.state.newUserRecaptchaToken
                      }
                      onClick={() => this.addNewUserToQueue()}
                      sx={{ alignSelf: "flex-end" }}
                    >
                      Adicionar à lista
                    </Button>
                  </Box>
                )}

                <Divider />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Serão atribuídos ao criar a aplicação:
                  </Typography>
                  {usersToAssign.length === 0 ? (
                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                      Nenhum respondente adicionado ainda.
                    </Typography>
                  ) : (
                    <Stack direction="column" gap={1}>
                      {usersToAssign.map((user) => (
                        <Chip
                          key={user.id}
                          label={
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              width="100%"
                            >
                              <Box component="span" flex={1}>
                                {user.type === "new"
                                  ? `${user.firstName} ${user.lastName} — convite (${user.email})`
                                  : `${user.firstName} ${user.lastName} (${user.email})`}
                              </Box>
                              {this.renderEmailStatusIcon(user)}
                            </Box>
                          }
                          onDelete={() => this.removeFromQueue(user.id)}
                          variant="outlined"
                          color={user.type === "new" ? "primary" : "default"}
                          sx={{
                            height: "auto",
                            width: "100%",
                            justifyContent: "space-between",
                            "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    );
  }

  renderForm() {
    const {
      instruments,
      selectedInstrument,
      inputValue,
      isSearching,
      allowFeedback,
      submissionPhase,
      inviteLog,
    } = this.state;
    const isCreating = submissionPhase === "creating";
    const isPostCreate = submissionPhase === "inviting" || submissionPhase === "done";
    const instrumentAllowFeedback = selectedInstrument?.allowFeedback ?? false;
    const instrumentDefaultFeedback = selectedInstrument?.defaultFeedback ?? false;
    const differsFromDefault =
      instrumentAllowFeedback && allowFeedback !== instrumentDefaultFeedback;

    return (
      <Box display="flex" gap={3} sx={{ minHeight: 380, flexDirection: { xs: "column", md: "row" } }}>
        {/* Left: application form */}
        <Box flex={1} display="flex" flexDirection="column" gap={2}>
          {isCreating && (
            <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Criando aplicação...</Typography>
            </Box>
          )}
          {isPostCreate && (
            <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
              <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
              <Typography variant="subtitle1" color="success.main" fontWeight="bold">Aplicação criada!</Typography>
            </Box>
          )}
          {!isCreating && !isPostCreate && (
            <>
              <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                Configurações da aplicação
              </Typography>

              {this.state.errorMessage && (
                <Alert
                  severity="error"
                  onClose={() => this.setState({ errorMessage: "" })}
                >
                  {this.state.errorMessage}
                </Alert>
              )}

              <TextField
                variant="outlined"
                required
                label="Nome da aplicação"
                value={this.state.assignmentName}
                sx={{ width: "100%" }}
                onChange={(event) => this.handleChangeName(event)}
              />

              <Autocomplete
                id="instrument-autocomplete"
                options={instruments}
                getOptionLabel={(instrument: IInstrument) =>
                  instrument.title || ""
                }
                value={selectedInstrument}
                inputValue={inputValue}
                filterOptions={(x) => x}
                loading={isSearching}
                onInputChange={(event, value) =>
                  this.handleInputChange(event, value)
                }
                onChange={(_event, newValue: IInstrument | null) => {
                  this.setState({
                    selectedInstrument: newValue,
                    allowFeedback: newValue?.defaultFeedback ?? false,
                  });
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Instrumento"
                    variant="outlined"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearching ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{ style: { maxHeight: 200 } }}
                noOptionsText="Nenhum instrumento encontrado"
                sx={{ width: "100%" }}
              />

              {selectedInstrument && selectedInstrument.quantity < 1 && (
                <Alert severity="error">
                  Este instrumento não possui licenças disponíveis. Não será possível criar a aplicação.
                </Alert>
              )}

              {instrumentAllowFeedback && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowFeedback}
                        onChange={(e) =>
                          this.setState({ allowFeedback: e.target.checked })
                        }
                      />
                    }
                    label="Permitir que o respondente acesse sua devolutiva individual ao terminar de responder"
                    sx={{ alignSelf: "flex-start" }}
                  />
                  {differsFromDefault && (
                    <Alert severity="warning">
                      {allowFeedback
                        ? "Com essa configuração, o respondente ACESSARÁ a devolutiva ao terminar de responder."
                        : "Com essa configuração, o respondente NÃO ACESSARÁ a devolutiva ao terminar de responder."}
                    </Alert>
                  )}
                </>
              )}

              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={
                  ptBR.components.MuiLocalizationProvider.defaultProps.localeText
                }
              >
                <MobileDateTimePicker
                  label="Data limite para conclusão da aplicação"
                  format="D/M/YYYY HH:mm"
                  ampm={false}
                  value={
                    this.state.selectedDate
                      ? dayjs(this.state.selectedDate)
                      : null
                  }
                  referenceDate={dayjs().hour(23).minute(59)}
                  onAccept={(date) => {
                    this.handleChangeDueDate(date);
                  }}
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: { variant: "outlined", required: true },
                  }}
                />
              </LocalizationProvider>
            </>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
        <Divider sx={{ display: { xs: "block", md: "none" } }} />

        {/* Right: user assignment panel */}
        <Box flex={1}>
          {isPostCreate && inviteLog.length > 0
            ? this.renderInviteLog()
            : this.renderUserPanel()}
        </Box>
      </Box>
    );
  }

  renderActions() {
    const { selectedInstrument, selectedDate, assignmentName } = this.state;
    return (
      <>
        <Button
          variant="outlined"
          onClick={() => this.didClickClose()}
          color="error"
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={() => this.didClickCreateAssignment()}
          disabled={
            !selectedDate ||
            !selectedInstrument ||
            assignmentName.length === 0 ||
            (selectedInstrument?.quantity ?? 1) < 1
          }
        >
          Criar aplicação
        </Button>
      </>
    );
  }

  render() {
    return (
      <Dialog
        open
        onClose={() => this.didClickClose()}
        variant="modal"
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle variant="modal">
          <AddIcon />
          Criar aplicação
        </DialogTitle>
        <DialogContent variant="modal">
          {this.state.isLoading
            ? this.renderLoading()
            : this.renderForm()}
        </DialogContent>
        {!this.state.isLoading && this.state.submissionPhase === "idle" && (
          <DialogActions variant="modal" sx={{ gap: 1, flexWrap: "wrap" }}>
            {this.renderActions()}
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default CreateAssignmentModal;
